import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import Reveal from "./Reveal";

const CATS = [
  { key: "all", labelKey: "cat_all" },
  { key: "raw", labelKey: "cat_raw" },
  { key: "food", labelKey: "cat_food" },
  { key: "spices", labelKey: "cat_spices" },
];

export default function Products() {
  const { t, lang } = useLang();
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("all");

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const filtered = active === "all" ? products : products.filter((p) => p.category === active);

  return (
    <section id="products" data-testid="products" className="relative bg-[#f6f6f4] py-24 md:py-32">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <Reveal>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#d81f2a]">
                {t("products_overline")}
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase leading-[0.95] tracking-tight text-[#0f1115]">
                {t("products_title")}
              </h2>
            </Reveal>
          </div>

          <div className="flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button
                key={c.key}
                data-testid={`filter-${c.key}`}
                onClick={() => setActive(c.key)}
                className={`px-4 py-2.5 font-mono text-xs uppercase tracking-[0.12em] border transition-colors ${
                  active === c.key
                    ? "bg-[#d81f2a] text-[#ffffff] border-[#d81f2a]"
                    : "text-[#5b6472] border-black/12 hover:border-black/30"
                }`}
              >
                {t(c.labelKey)}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.div
                layout
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
                data-testid={`product-card-${p.id}`}
                className={`group relative overflow-hidden border border-black/10 bg-[#ffffff] ${
                  i % 5 === 0 ? "lg:row-span-2" : ""
                }`}
              >
                <div className={`relative overflow-hidden ${i % 5 === 0 ? "h-72 lg:h-full lg:min-h-[420px]" : "h-64"}`}>
                  <img
                    src={p.image}
                    alt={p.name?.[lang] || p.name?.tr}
                    className="w-full h-full object-cover spotlight group-hover:scale-110 grayscale group-hover:grayscale-0"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#ffffff] via-[#ffffff]/20 to-transparent" />
                  {p.brand && (
                    <span className="absolute top-4 left-4 bg-[#d81f2a] text-[#ffffff] px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest font-bold">
                      {p.brand}
                    </span>
                  )}
                  <ArrowUpRight className="absolute top-4 right-4 w-6 h-6 text-[#0f1115]/40 group-hover:text-[#d81f2a] group-hover:rotate-0 -rotate-45 transition-all" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-display text-xl md:text-2xl font-bold uppercase tracking-tight text-[#0f1115] leading-tight">
                    {p.name?.[lang] || p.name?.tr}
                  </h3>
                  <p className="mt-2 font-body text-sm text-[#5b6472] line-clamp-2">
                    {p.description?.[lang] || p.description?.tr}
                  </p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
